import {
  ChangeDetectionStrategy,
  Component,
  input,
  OnDestroy,
  OnInit,
  signal,
  output,
} from '@angular/core';
import {
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormControl,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  startWith,
  Subscription,
  throttleTime,
} from 'rxjs';
import { LogService } from '../../core/log/log.service';
import { Category } from '../../domain/category/category.model';
import { City } from '../../domain/city/city.model';
import { Province } from '../../domain/province/province.model';
import { Specialty } from '../../domain/specialty/specialty.model';
import { GUEST_USER, ListaGruppiEnum, UserDTO } from '../../domain/user/user.model';
import { selectUser } from '../../domain/user/user.selectors';
import { ChangePasswordDialogComponent } from '../dialog/change-password-dialog/change-password-dialog.component';
import { ConsentInfoDialogComponent } from '../dialog/consent-info-dialog/consent-info-dialog.component';
import { openDialog } from '../dialog/dn-dialog.actions';
import { evaluateOneOrZeroFromBoolean } from '../util/util';
import {
  AUTOCOMPLETE_MIN_LETTERS,
  DOCTOR_ID,
  IDS_TO_SHOW_CATEGORY_FIELDS,
  UserForm,
  UserFormConsentsForm,
  UserFormEcmInfos,
  UserFormPersonalInfos,
  UserFormProfessionalInfos,
  UserFormProfessionalInfosRegisterForm,
  UserFormType,
} from './user-form.model';
import { UserFormService } from './user-form.service';
import { autocompleteObjectValidator } from '../util/validators';
import { EnvironmentService } from '../../../environments/environment.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatOptionModule,
} from '@angular/material/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatRadioModule } from '@angular/material/radio';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { AsyncPipe, NgClass } from '@angular/common';
import { UserService } from '../../domain/user/user.service';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ProfessionType } from '../../domain/profession/profession.model';
import { AgenasDisciplineType } from 'src/app/domain/agenas-discipline/agenas-discipline.model';
import { CustomDateAdapter } from '../util/custom-date-adapter/custom-date-adapter';

export const APP_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'input',
    monthYearLabel: { month: 'short', year: 'numeric' },
    dateA11yLabel: { day: 'numeric', month: 'long', year: 'numeric' },
    monthYearA11yLabel: { month: 'long', year: 'numeric' },
  },
};

export function userFormServiceFactory(
  ts: TranslateService,
  fb: FormBuilder,
  userService: UserService,
  store: Store
) {
  return new UserFormService(ts, fb, userService, store);
}

@Component({
  selector: 'dottnet-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatRadioModule,
    MatDatepickerModule,
    MatAutocompleteModule,
    MatOptionModule,
    MatIconModule,
    AsyncPipe,
    TranslateModule,
    MatCheckboxModule,
  ],
  providers: [
    /* {
        provide: UserFormService,
        useFactory: userFormServiceFactory,
        deps: [ TranslateService, FormBuilder, UserService, Store]
    }, */

    {
      provide: MAT_DATE_LOCALE,
      useFactory: (env: EnvironmentService) => env.defaultLanguage,
      deps: [EnvironmentService],
    },
    {
      provide: DateAdapter,
      useClass: CustomDateAdapter,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: APP_DATE_FORMATS,
    },
  ],
})
export class UserFormComponent implements OnInit, OnDestroy {
  // Inputs convertiti in signal
  readonly user = input<UserDTO | undefined>(undefined);
  readonly categoryOptions = input<Category[]>([]);
  readonly specialtyOptions = input<Specialty[]>([]);
  readonly professionTypesOptions = input<ProfessionType[]>([]);
  readonly agenasDisciplineTypesOptions = input<AgenasDisciplineType[]>([]);
  readonly provinceOptions = input<Province[]>([]);
  readonly listaGruppi = input<ListaGruppiEnum[]>([]);
  readonly formType = input<UserFormType>(UserFormType.SIGNUP);
  readonly isSubmitting = input<boolean>(false);
  readonly showPersonal = input<boolean>(false);
  readonly showProfessional = input<boolean>(false);
  readonly showConsents = input<boolean>(false);
  readonly showEmail = input<boolean>(false);
  readonly showPwd = input<boolean>(false);
  readonly showEcm = input<boolean>(false);
  readonly ecmChecked = input<boolean>(false);

  // Internal state signal
  isecmCheckedInternal = signal(false);

  // City options signal
  readonly cityOptions = input<City[]>([]);

  // Request form signal (sostituisce il precedente Observable)
  readonly requestForm = input<boolean>(false);

  readonly lookupCities = output<string>();
  readonly submitForm = output<FormGroup<UserForm>>();

  readonly sendForm = output<FormGroup<UserForm>>();

  // Main form group
  componentForm: FormGroup<UserForm>;

  // Filtered options for autocompletes
  categoryFilteredOptions$: Observable<Category[]> = new Observable<Category[]>();
  specialtyFilteredOptions$: Observable<Specialty[]> = new Observable<Specialty[]>();
  provinceFilteredOptions$: Observable<Province[]> = new Observable<Province[]>();
  professionTypesFilteredOptions$: Observable<ProfessionType[]> = new Observable<
    ProfessionType[]
  >();
  agenasDisciplineTypesFilteredOptions$: Observable<AgenasDisciplineType[]> = new Observable<
    AgenasDisciplineType[]
  >();

  // Subcriptions
  userSub: Subscription = new Subscription();
  birthplaceFieldSub: Subscription = new Subscription();
  computeCFSub: Subscription = new Subscription();

  privacyPolicyUrl: string;
  imprintUrl: string;

  constructor(
    private formService: UserFormService,
    private logService: LogService,
    private store: Store,
    private UserService: UserService,
    private environmentService: EnvironmentService
  ) { }

  ngOnInit() {
    this.privacyPolicyUrl = this.environmentService.privacyPolicyUrl;
    this.imprintUrl = this.environmentService.imprintUrl;
    this.componentForm = this.buildForm(this.listaGruppi());
    this.isecmCheckedInternal.set(this.ecmChecked());

    this.componentForm.updateValueAndValidity();

    this.userSub = this.store
      .select(selectUser)
      .pipe(filter((user) => user && user.idAnagrafica !== GUEST_USER.idAnagrafica))
      .subscribe((user) => this.precompileForm(user));

    // Setup form filters for autocomplete
    this.setupFilters();

    // Set up form validation
    this.setupFormValidation();

    // Check for request form changes from input
    this.effect(() => {
      if (this.requestForm()) {
        this.componentForm.markAllAsTouched();
        this.sendForm.emit(this.componentForm);
      }
    });
  }

  // Setup filter observables for autocomplete components
  private setupFilters() {
    // Setup categories filter
    this.categoryFilteredOptions$ = this.category.valueChanges.pipe(
      startWith<string | Category>(''),
      map((value) => this.filterCategory(value, this.categoryOptions()))
    );

    // Setup specialties filter
    this.specialtyFilteredOptions$ = this.specialty.valueChanges.pipe(
      startWith<string | Specialty>(''),
      map((value) => this.filterSpecialty(value, this.specialtyOptions()))
    );

    // Setup profession types filterok qu
    this.professionTypesFilteredOptions$ = this.professionType.valueChanges.pipe(
      startWith<string | ProfessionType>(''),
      map((value) => this.filterProfessionTypes(value, this.professionTypesOptions()))
    );

    // Setup agenas discipline types filter
    this.agenasDisciplineTypesFilteredOptions$ = this.agenasDisciplineType.valueChanges.pipe(
      startWith<string | AgenasDisciplineType>(''),
      map((value) => this.filterAgenasDisciplineTypes(value, this.agenasDisciplineTypesOptions()))
    );

    // Setup province filter
    this.provinceFilteredOptions$ = this.registerProvince.valueChanges.pipe(
      startWith<string | Province>(''),
      map((value) => this.filterProvince(value, this.provinceOptions()))
    );
  }

  // Setup form validation and reactivity
  private setupFormValidation() {
    // Listen for event to update CF
    this.computeCFSub = this.personalInfos.valueChanges
      .pipe(
        debounceTime(200), // little debounce time because if the user type fast, the chain starts anc the form doesn't show the correct cf value
        throttleTime(250),
        distinctUntilChanged((before, after) => JSON.stringify(before) === JSON.stringify(after))
      )
      .subscribe(() => this.calcolaCod());
  }

  // Setup reactive effects for signals
  private effect(callback: () => void) {
    // Simple effect implementation for signal reactivity
    callback();
  }

  onPlaceChange(fieldValue: Event) {
    const selectedPlace = (fieldValue.target as HTMLInputElement).value;

    if (selectedPlace) {
      if (selectedPlace.length > AUTOCOMPLETE_MIN_LETTERS) {
        this.logService.info('Emitting lookupCities with value', fieldValue);
        this.lookupCities.emit(selectedPlace);
      }
    }
  }

  onSpecChange(event: any) {
    const selectedSpecialty = event.option.value as Specialty;

    if (selectedSpecialty && selectedSpecialty.idDisciplinaAgenas) {
      const matchingDiscipline = this.agenasDisciplineTypesOptions().find(
        (d) => d.idDisciplinaAgenas === selectedSpecialty.idDisciplinaAgenas
      );

      if (matchingDiscipline) {
        this.agenasDisciplineType.setValue(matchingDiscipline);
      }
    }
  }

  // Filter function to lookup for matches in category typeahead
  filterCategory(value: any, categoryOptions: Category[]): Category[] {
    const filterValue = value.descrizioneCategoria || value.toLowerCase().trim();
    return categoryOptions.filter((option) =>
      option.descrizioneCategoria.toLowerCase().includes(filterValue)
    );
  }

  // Filter function to lookup for matches in specialty typeahead
  filterSpecialty(value: any, specialtyOptions: Specialty[]): Specialty[] {
    const filterValue = value.descrizioneSpecializzazione || value.toLowerCase().trim();
    return specialtyOptions.filter((option) =>
      option.descrizioneSpecializzazione.toLowerCase().includes(filterValue)
    );
  }

  // Filter function to lookup for matches in province typeahead
  filterProvince(value: any, provinceOptions: Province[]): Province[] {
    const filterValue = value.descrizioneProvincia || value.toLowerCase().trim();
    return provinceOptions.filter((option) =>
      option.descrizioneProvincia.toLowerCase().includes(filterValue)
    );
  }

  // Filter function to lookup for matches in ProfessionTypes typeahead
  filterProfessionTypes(value: any, professionTypesOptions: ProfessionType[]): ProfessionType[] {
    const filterValue = value.descrizioneTipoProfessione || value.toLowerCase().trim();
    return professionTypesOptions.filter((option) =>
      option.descrizioneTipoProfessione.toLowerCase().includes(filterValue)
    );
  }

  // Filter function to lookup for matches in AgenasDisciplineTypes typeahead
  filterAgenasDisciplineTypes(
    value: any,
    agenasDisciplineTypesOptions: AgenasDisciplineType[]
  ): AgenasDisciplineType[] {
    const filterValue = value.descrizioneDisciplina || value.toLowerCase().trim();
    return agenasDisciplineTypesOptions.filter((option) =>
      option.descrizioneDisciplinaAgenas.toLowerCase().includes(filterValue)
    );
  }

  ngOnDestroy(): void {
    if (this.userSub) this.userSub.unsubscribe();
    if (this.birthplaceFieldSub) this.birthplaceFieldSub.unsubscribe();
    if (this.computeCFSub) this.computeCFSub.unsubscribe();
  }

  get email() {
    return this.componentForm.get('personalInfos').get('email') as FormControl<string>;
  }

  get password() {
    return this.componentForm.get('personalInfos').get('password') as FormControl<string>;
  }

  get name() {
    return this.componentForm.get('personalInfos').get('name') as FormControl<string>;
  }

  get surname() {
    return this.componentForm.get('personalInfos').get('surname') as FormControl<string>;
  }

  get gender() {
    return this.componentForm.get('personalInfos').get('gender') as FormControl<string>;
  }

  get birthplace() {
    return this.componentForm.get('personalInfos').get('birthplace') as FormControl<City>;
  }

  get birthdate() {
    return this.componentForm.get('personalInfos').get('birthdate') as FormControl<string>;
  }

  get bornAbroad() {
    return this.componentForm.get('personalInfos').get('bornAbroad') as FormControl<string>;
  }

  get codiceFiscale() {
    return this.componentForm.get('personalInfos').get('codiceFiscale') as FormControl<string>;
  }

  get category() {
    return this.componentForm?.get('professionalInfos')?.get('category') as FormControl<Category>;
  }

  get specialty() {
    return this.componentForm.get('professionalInfos').get('specialty') as FormControl<Specialty>;
  }

  get registerNumber() {
    return this.componentForm
      .get('professionalInfos')
      .get('registerForm')
      .get('registerNumber') as FormControl<string>;
  }

  get registerProvince() {
    return this.componentForm
      .get('professionalInfos')
      .get('registerForm')
      .get('registerProvince') as FormControl<Province>;
  }

  get cellulare() {
    return this.componentForm.get('ecmInfos').get('cellulare') as FormControl<string>;
  }

  get professionPlace() {
    return this.componentForm.get('ecmInfos').get('professionPlace') as FormControl<City>;
  }

  get professionType() {
    return this.componentForm.get('ecmInfos').get('professionType') as FormControl<ProfessionType>;
  }

  get agenasDisciplineType() {
    return this.componentForm
      .get('ecmInfos')
      .get('agenasDiscipline') as FormControl<AgenasDisciplineType>;
  }

  get marketing() {
    return this.consentsForm.get('marketing') as FormControl<string>;
  }

  get profiling() {
    return this.consentsForm.get('profiling') as FormControl<string>;
  }

  get marketingThirdParties() {
    return this.consentsForm.get('marketingThirdParties') as FormControl<string>;
  }

  get profilingThirdParties() {
    return this.consentsForm.get('profilingThirdParties') as FormControl<string>;
  }

  get cap() {
    return this.componentForm.get('ecmInfos').get('cap') as FormControl<string>;
  }

  //GET of FormGroups

  get personalInfos(): FormGroup<UserFormPersonalInfos> {
    return this.componentForm.get('personalInfos') as FormGroup<UserFormPersonalInfos>;
  }
  get professionalInfos(): FormGroup<UserFormProfessionalInfos> {
    return this.componentForm.get('professionalInfos') as FormGroup<UserFormProfessionalInfos>;
  }

  get registerForm(): FormGroup<UserFormProfessionalInfosRegisterForm> {
    return this.componentForm
      .get('professionalInfos')
      .get('registerForm') as FormGroup<UserFormProfessionalInfosRegisterForm>;
  }

  get ecmInfos(): FormGroup<UserFormEcmInfos> {
    return this.componentForm.get('ecmInfos') as FormGroup<UserFormEcmInfos>;
  }

  get consentsForm(): FormGroup<UserFormConsentsForm> {
    return this.componentForm.get('consentsForm') as FormGroup<UserFormConsentsForm>;
  }

  errorMessage(field: string): string {
    return this.formService.buildErrorMessage<UserForm>(this.componentForm, field);
  }

  private buildForm(listaGruppi: ListaGruppiEnum[]): FormGroup<UserForm> {
    return this.formType() === UserFormType.EDIT
      ? this.formService.buildEditUserForm(listaGruppi)
      : this.formService.buildRegisterUserForm();
  }

  private precompileForm(user: UserDTO) {
    this.cellulare.setValue(user.cellulare);
    this.cap.setValue(user.cap);
    this.professionPlace.setValue(<City>{
      idComune: user.idComuneEsercizio,
      descrizioneComune: user.comuneEsercizio,
      belfiore: user.codiceBelfioreEsercizio,
    });
    this.professionType.setValue({
      idTipoProfessione: user.idTipoProfessione,
      descrizioneTipoProfessione: user.tipoProfessione,
    });
    this.agenasDisciplineType.setValue({
      idDisciplinaAgenas: user.idDisciplinaAgenas,
      descrizioneDisciplinaAgenas: user.disciplinaAgenas,
    });

    if (user.cellulare?.length > 0) {
      this.isecmCheckedInternal.set(true);
    }

    this.name.setValue(user.nome);
    this.surname.setValue(user.cognome);

    // La mail non è editabile
    this.email.setValue(user.email);
    this.email.disable();

    this.gender.setValue(user.sesso);
    this.birthdate.setValue(user.dataNascita?.toString());

    this.birthplace.setValue(<City>{
      idComune: user.idComuneNascita,
      descrizioneComune: user.comuneNascita,
      belfiore: user.codiceBelfiore,
    });

    if (user.comuneNascita) {
      if (user.idComuneNascita === 0) {
        // ESTERO
        this.bornAbroad.setValue('1');
      } else {
        this.bornAbroad.setValue('0');
      }
      // this.codiceFiscale.disable();
    } else {
      this.bornAbroad.setValue('0');
      // se sto mostrando i dati personali gestisco il flag di birthplace per comandare la validazione
      if (this.showPersonal()) {
        this.manageBirthplaceField();
        this.codiceFiscale.addAsyncValidators(this.UserService.checkCFExistence(this.store));
      }
    }

    // se posso calcolare il codice fiscale lo calcolo altrimenti prendo quello eventualmente memorizzato a db
    if (this.isCFComputable()) {
      this.codiceFiscale.setValue(
        this.formService.buildCodiceFiscale(
          this.name.value,
          this.surname.value,
          this.birthplace.value,
          this.birthdate.value,
          this.gender.value
        )
      );
    } else {
      this.codiceFiscale.setValue(user.codiceFiscale);
    }

    // Qui forziamo i validator in EDIT se serve
    if (this.formType() === UserFormType.EDIT) {
      this.codiceFiscale.setValidators([Validators.required]);
      this.codiceFiscale.updateValueAndValidity();
    }

    if (user.idCategoria) {
      this.category.setValue({
        idCategoria: user.idCategoria,
        descrizioneCategoria: user.categoria,
      });

      if (user.idSpecializzazione) {
        this.specialty.setValue({
          idSpecializzazione: user.idSpecializzazione,
          descrizioneSpecializzazione: user.specializzazione,
          idDisciplinaAgenas: user.idDisciplinaAgenas,
        });
      }

      if (this.isDoctorSelected()) {
        this.specialty.addValidators(Validators.required);
        this.specialty.addValidators(autocompleteObjectValidator());
      } else this.specialty.clearValidators();

      if (this.areShownCategoryFields() && this.showProfessional()) {
        this.registerNumber.addValidators(Validators.required);
        this.registerProvince.addValidators(Validators.required);
        this.registerProvince.addValidators(autocompleteObjectValidator());
      }
    }

    this.registerNumber.setValue(user.numeroIscrizioneAlbo?.toString());

    // Trova la provincia corrispondente
    const matchingProvince = this.provinceOptions().find(
      (province) => province.siglaProvincia === user.provinciaIscrizioneAlbo
    );

    if (matchingProvince) {
      this.registerProvince.setValue(matchingProvince);
    }

    this.marketing.setValue(evaluateOneOrZeroFromBoolean(user.marketing));
    this.marketingThirdParties.setValue(evaluateOneOrZeroFromBoolean(user.marketingTerzi));
    this.profiling.setValue(evaluateOneOrZeroFromBoolean(user.profilazione));
    this.profilingThirdParties.setValue(evaluateOneOrZeroFromBoolean(user.profilazioneTerzi));

    this.componentForm.markAllAsTouched();

    if (this.formType() === UserFormType.EDIT && this.componentForm.valid) {
      // in edit if status is valid I have to mark form as dirty in order to activate save button
      this.componentForm.markAsDirty();
    }
  }

  displayCategory(elem: Category) {
    return elem.descrizioneCategoria;
  }

  displaySpecialty(elem: Specialty): string {
    return elem.descrizioneSpecializzazione;
  }

  displayProvince(elem: Province): string {
    return elem.descrizioneProvincia;
  }

  displayCity(elem: City): string {
    return elem.descrizioneComune;
  }

  displayProfessionType(elem: ProfessionType): string {
    return elem.descrizioneTipoProfessione;
  }

  displayAgenasDisciplineType(elem: AgenasDisciplineType): string {
    return elem.descrizioneDisciplinaAgenas;
  }

  manageBirthplaceField() {
    if (this.bornAbroad.value === '1') {
      this.birthplace.setValue(<City>{
        idComune: 0,
        descrizioneComune: 'ESTERO',
      });
      this.birthplace.disable();
      this.birthplace.clearValidators();
      this.birthplace.updateValueAndValidity();
      // this.codiceFiscale.enable();
      this.codiceFiscale.setValue('');
    } else {
      this.birthplace.setValue(<City>{});
      this.birthplace.enable();
      this.birthplace.addValidators([autocompleteObjectValidator(), Validators.required]);
      this.birthplace.updateValueAndValidity();
      // this.codiceFiscale.disable();
      // this.codiceFiscale.setValue('');
    }
  }

  setEcmChecked() {
    this.isecmCheckedInternal.set(!this.isecmCheckedInternal());

    if (this.isecmCheckedInternal()) {
      this.cellulare.setValidators([Validators.required, Validators.pattern('[- +()0-9]{9,12}')]);
      this.cellulare.updateValueAndValidity();
      this.professionPlace.setValidators([autocompleteObjectValidator(), Validators.required]);
      this.professionPlace.addValidators([autocompleteObjectValidator(), Validators.required]);
      this.professionPlace.updateValueAndValidity();
      this.professionType.setValidators([autocompleteObjectValidator(), Validators.required]);
      this.professionType.addValidators([autocompleteObjectValidator(), Validators.required]);
      this.professionType.updateValueAndValidity();
      this.agenasDisciplineType.setValidators([autocompleteObjectValidator(), Validators.required]);
      this.agenasDisciplineType.addValidators([autocompleteObjectValidator(), Validators.required]);
      this.agenasDisciplineType.updateValueAndValidity();
      this.cap.setValidators([Validators.required, Validators.pattern('^[0-9]*$')]);
      this.cap.updateValueAndValidity();
    } else {
      this.cellulare.clearValidators();
      this.cellulare.updateValueAndValidity();
      this.professionPlace.clearValidators();
      this.professionPlace.updateValueAndValidity();
      this.professionType.clearValidators();
      this.professionType.updateValueAndValidity();
      this.agenasDisciplineType.clearValidators();
      this.agenasDisciplineType.updateValueAndValidity();
      this.cap.clearValidators();
      this.cap.updateValueAndValidity();
    }
  }

  manageCategoryFields() {
    if (this.areShownCategoryFields()) {
      this.registerNumber.addValidators(Validators.required);
      this.registerProvince.addValidators(Validators.required);
      this.registerProvince.addValidators(autocompleteObjectValidator());

      if (this.isDoctorSelected()) {
        this.specialty.addValidators(Validators.required);
        this.specialty.addValidators(autocompleteObjectValidator());
      } else {
        this.specialty.clearValidators();
      }
    } else {
      this.registerNumber.setValue('');
      this.registerProvince.setValue(<Province>{});
      this.registerNumber.clearValidators();
      this.registerProvince.clearValidators();
      this.specialty.clearValidators();
    }
    this.registerNumber.updateValueAndValidity();
    this.registerProvince.updateValueAndValidity();
    this.specialty.updateValueAndValidity();
  }

  areShownCategoryFields() {
    return IDS_TO_SHOW_CATEGORY_FIELDS.includes(this.category.value.idCategoria);
  }

  isDoctorSelected() {
    return DOCTOR_ID === this.category.value.idCategoria;
  }

  emitSubmit() {
    this.submitForm.emit(this.componentForm);
    this.componentForm.markAsPristine();
  }

  calcolaCod() {
    if (this.isCFComputable()) {
      this.codiceFiscale.setValue(
        this.formService.buildCodiceFiscale(
          this.name.value,
          this.surname.value,
          this.birthplace.value,
          this.birthdate.value,
          this.gender.value
        )
      );
    } else if (this.bornAbroad.value === '0') {
      this.codiceFiscale.setValue('');
    }
  }

  isCFComputable() {
    return (
      this.bornAbroad.value === '0' &&
      this.name.value &&
      this.surname.value &&
      this.birthplace?.value?.descrizioneComune &&
      this.birthdate.value &&
      this.gender.value
    );
  }

  changePassword() {
    this.store.dispatch(
      openDialog({
        content: undefined,
        urlToDownload: undefined,
        urlWithContext: undefined,
        componentType: ChangePasswordDialogComponent,
        panelClass: 'dn-dialog-small',
        data: { idAnagrafica: this.user()?.idAnagrafica },
      })
    );
  }

  // Dialog
  openDialog(
    consentType: 'marketing' | 'marketingThirdParties' | 'profiling' | 'profilingThirdParties'
  ) {
    this.store.dispatch(
      openDialog({
        content: undefined,
        urlToDownload: undefined,
        urlWithContext: undefined,
        componentType: ConsentInfoDialogComponent,
        panelClass: 'dn-dialog-medium',
        data: { consentType: consentType },
      })
    );
  }
}
